/* eslint-disable no-param-reassign */
import produce from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'underscore';

import type { ApiPriceData, ApiProduct } from '../../../models/ecommerce/product/ApiProduct';
import { SyncronQueue } from '../../common/api/SyncronQueue';
import { productFlow } from '../../common/flow/product/productFlow';
import { useAsyncDispatcher } from '../../common/hooks/useAsyncDispatcher';
import { useGroupErrorEffect } from '../../common/hooks/useGroupError';
import { useAppSelector } from '../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../common/selectors/storeSelectors';
import { ErrorGroup } from '../../common/store/structureDefinitions/errorsState';
import { ga4RecipesTracking } from '../../common/tracking/ga4/recipes';
import { getRecipeSection } from '../../common/tracking/ga4/recipes/ga4Recipes.utils';
import recipeFlow from '../flow/recipeFlow';
import type {
    RecipeCalculationType,
    RecipeItemState,
    RecipePrioritizationType,
    RecipeProductException,
    RecipeProductGroupType,
    RecipeProductItem,
    RecipeSectionType,
    ResolvedRecipe,
    ResolvedRecipesList,
    ResolveRecipesRequest,
} from '../models';
import { ItemState } from '../models';

export interface JoinedRecipe {
    name: string;
    id: string;
}

export interface RecipeItemProductSubstitute extends RecipeItemState {
    product: ApiProduct;
}

export const mapItem = (
    recipeProductItem: RecipeProductItem,
    group: RecipeProductGroupType,
    isRemoved: boolean,
): RecipeItemState => {
    return {
        id: recipeProductItem.id,
        priceData: recipeProductItem.priceData,
        purchasable: recipeProductItem.purchasable,
        quantity: recipeProductItem.quantity,
        substituteProductsAmount: recipeProductItem.substituteProductsAmount,
        image: recipeProductItem.image,
        manufacturer: recipeProductItem.manufacturer,
        name: recipeProductItem.name,
        substituteIdentifier: recipeProductItem.substituteIdentifier,
        packageSizeInformation: recipeProductItem.packageSizeInformation,
        belongsToRecipes:
            recipeProductItem.belongsToRecipes?.map((item) => ({
                name: item.name,
                code: item.recipeId,
            })) || [],
        selectedVarianceCode: recipeProductItem.selectedVarianceCode,
        totalPriceData: recipeProductItem.rowTotalPriceData,
        group,
        state: isRemoved ? ItemState.Removed : ItemState.Default,
    };
};

export type RecipeSection =
    | ({
          type: Extract<RecipeSectionType, 'common'>;
      } & Omit<Section, 'portions'>)
    | ({
          type: Extract<RecipeSectionType, 'recipe'>;
      } & Section)
    | ({
          type: Extract<RecipeSectionType, 'shared'>;
      } & Omit<Section, 'portions'>);

export interface Section {
    id: string; // could be shared or common
    type: RecipeSectionType;
    name: string;
    imageUrl: string;
    portions: number;
    substituteIdentifiers: string[];
    productIdentifiers: string[];
    sharedSubstituteIdentifiers?: string[];
}

const mapItems = (
    hybrisRecipeList: ResolvedRecipesList,
    removedSubsituteIdentifiers: string[],
): RecipeItemState[] => {
    const singleProducts = (hybrisRecipeList.singleProducts || []).map((item) =>
        mapItem(
            item,
            'singleProducts',
            removedSubsituteIdentifiers.includes(item.substituteIdentifier),
        ),
    );
    const sharedProducts = (hybrisRecipeList.sharedProducts || []).map((item) =>
        mapItem(
            item,
            'sharedProducts',
            removedSubsituteIdentifiers.includes(item.substituteIdentifier),
        ),
    );
    const goodToHaveProducts = (hybrisRecipeList.goodToHaveProducts || []).map((item) =>
        mapItem(
            item,
            'commonProducts',
            removedSubsituteIdentifiers.includes(item.substituteIdentifier),
        ),
    );

    const allItems = [...singleProducts, ...sharedProducts, ...goodToHaveProducts];
    return allItems;
};

const mapSections = (
    recipeList: ResolvedRecipesList,
    removedSubsituteIdentifiers: string[],
): RecipeSection[] => {
    const singleProducts = (recipeList.singleProducts || []).filter(
        (item) => !removedSubsituteIdentifiers.includes(item.substituteIdentifier),
    );
    const sharedProducts = recipeList.sharedProducts.filter(
        (item) => !removedSubsituteIdentifiers.includes(item.substituteIdentifier),
    );
    const goodToHaveProducts = recipeList.goodToHaveProducts.filter(
        (item) => !removedSubsituteIdentifiers.includes(item.substituteIdentifier),
    );

    const recipeSectionsMap = singleProducts.reduce<Record<string, RecipeSection>>(
        (prev, next) => {
            const sectionId = next.belongsToRecipes[0].recipeId;
            const sectionName = next.belongsToRecipes[0].name;
            const recipe = recipeList.recipes.find((_recipe) => _recipe.code === sectionId);

            const final = { ...prev };

            if (recipe) {
                final[sectionId] = {
                    id: sectionId,
                    type: 'recipe',
                    name: sectionName,
                    imageUrl: recipe.imageUrl,
                    portions: recipe.portions,
                    substituteIdentifiers: prev[sectionId]?.substituteIdentifiers
                        ? [...prev[sectionId].substituteIdentifiers, next.substituteIdentifier]
                        : [next.substituteIdentifier],
                    productIdentifiers: prev[sectionId]?.productIdentifiers
                        ? [...prev[sectionId].productIdentifiers, next.id]
                        : [next.id],
                };
            }

            return final;
        },
        {} as Record<string, RecipeSection>,
    );

    // add info how many products are in shared section for each recipe
    Object.keys(recipeSectionsMap).forEach((sectionId) => {
        const sharedProductsBelongingToRecipe = sharedProducts
            .filter((sharedProduct) =>
                sharedProduct.belongsToRecipes
                    .map((joinedRecipe) => joinedRecipe.recipeId)
                    .includes(sectionId),
            )
            .map((product) => product.substituteIdentifier);

        recipeSectionsMap[sectionId].sharedSubstituteIdentifiers = sharedProductsBelongingToRecipe;
    });

    const sharedRecipeProductIds = sharedProducts.map((x) => x.id);
    const goodToHaveProductIds = goodToHaveProducts.map((x) => x.id);

    const sharedRecipeSubstituteIds = sharedProducts.map((x) => x.substituteIdentifier);
    const goodToHaveSubstituteIds = goodToHaveProducts.map((x) => x.substituteIdentifier);

    const recipeSectionList = Object.values<RecipeSection>(recipeSectionsMap);

    if (sharedRecipeSubstituteIds.length) {
        recipeSectionList.push({
            id: 'shared',
            type: 'shared',
            name: 'Delas mellan recepten',
            substituteIdentifiers: sharedRecipeSubstituteIds,
            productIdentifiers: sharedRecipeProductIds,
            imageUrl: '/Assets/Images/recipe-shared.png',
        });
    }

    if (goodToHaveProductIds.length) {
        recipeSectionList.push({
            id: 'common',
            type: 'common',
            name: 'Basvaror du behöver ha hemma',
            substituteIdentifiers: goodToHaveSubstituteIds,
            productIdentifiers: goodToHaveProductIds,
            imageUrl: '/Assets/Images/recipe-good-to-have.png',
        });
    }

    return recipeSectionList;
};

const createCartState = (
    recipeList: ResolvedRecipesList,
    removedItems: RecipeItemState[],
): RecipeCartState => {
    const removedSubstituteIds = removedItems.map((item) => item.substituteIdentifier);
    const sections = mapSections(recipeList, removedSubstituteIds);

    return {
        calculation: recipeList.calculation,
        prioritization: recipeList.prioritization,
        recipes: recipeList.recipes,
        items: mapItems(recipeList, removedSubstituteIds),
        sections,
        totalPriceData: recipeList.recipesTotalPrice,
    };
};

const recipeCartQueue = new SyncronQueue();

interface RecipeData {
    recipeId: string;
    portions: number;
}

export interface RecipeCartState {
    recipes: ResolvedRecipe[];
    prioritization?: RecipePrioritizationType;
    calculation: RecipeCalculationType;
    items: RecipeItemState[];
    sections: RecipeSection[];
    totalPriceData: ApiPriceData;
}

interface RecipeItemChangeState extends RecipeItemState {
    removed?: boolean;
}

interface RecipeCartChangesState {
    recipes: RecipeData[];
    prioritization: RecipePrioritizationType | null;
    calculation: RecipeCalculationType;
    productChanges: Record<string, RecipeItemChangeState>;
}

const mapProductChangesToExceptions = (productChanges: Record<string, RecipeItemState>) => {
    if (!productChanges) return [];

    const changedProducts = Object.values(productChanges);
    const productExceptions = changedProducts.map<RecipeProductException>((cp) => ({
        productId: cp.id,
        substituteId: cp.substituteIdentifier,
        qty: cp.quantity,
    }));

    return productExceptions;
};

const useRecipeCart = (initialLoading: boolean = true) => {
    const [cartState, setCartState] = useState<RecipeCartState | null>(null);
    const [cartChangesState, setCartChangesState] = useState<RecipeCartChangesState | null>(null);
    const removedItems = useRef<RecipeItemState[]>([]);

    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const updateSubstituteProduct = useCallback((item: RecipeItemState, newProductId: string) => {
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => {
                if (draft) {
                    draft.productChanges[item.substituteIdentifier] = {
                        ...item,
                        id: newProductId,
                        quantity: null,
                    };
                }

                return draft;
            });
        });

        const recipeSection = getRecipeSection(item);

        ga4RecipesTracking.weeklyMenu.handleIngredient('change item')?.(
            recipeSection.recipeId,
            recipeSection.recipeName,
            item.id,
            item.name,
        );
    }, []);

    const updateItemQuantity = useCallback((item: RecipeItemState, newQuantity: number) => {
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => {
                if (draft) {
                    draft.productChanges[item.substituteIdentifier] = {
                        ...item,
                        quantity: newQuantity,
                    };
                }
                return draft;
            });
        });

        const recipeSection = getRecipeSection(item);

        if (item.quantity === null) {
            return;
        }

        if (newQuantity > item.quantity) {
            // add
            ga4RecipesTracking.weeklyMenu.handleIngredient('add')?.(
                recipeSection.recipeId,
                recipeSection.recipeName,
                item.id,
                item.name,
            );
        }

        if (newQuantity < item.quantity) {
            // remove
            ga4RecipesTracking.weeklyMenu.handleIngredient('remove')?.(
                recipeSection.recipeId,
                recipeSection.recipeName,
                item.id,
                item.name,
            );
        }
    }, []);

    const updatePortions = useCallback((recipeId: string, portions: number) => {
        removedItems.current = [];
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => {
                if (draft) {
                    Object.values(draft.productChanges).forEach((pc) => {
                        pc.quantity = null;
                    });
                    const recipeToChange = draft.recipes.find((cr) => cr.recipeId === recipeId);
                    if (recipeToChange) {
                        recipeToChange.portions = portions;
                    }
                }

                return draft;
            });
        });
    }, []);

    const updateCalculation = useCallback(
        (
            calculationType: RecipeCalculationType,
            prioritizationType: RecipePrioritizationType | null,
        ) => {
            removedItems.current = [];
            setCartChangesState((prevChanges) => {
                return produce(prevChanges, (draft) => {
                    if (draft) {
                        draft.productChanges = {}; // what to do here, keep substitues or always recalculate?
                        draft.calculation = calculationType;
                        draft.prioritization = prioritizationType;
                    }

                    return draft;
                });
            });
        },
        [],
    );

    const calculateCart = useCallback((request: ResolveRecipesRequest) => {
        removedItems.current = [];
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => ({
                ...draft,
                calculation: request.calculation,
                prioritization: request.prioritization,
                recipes: request.recipes,
                productChanges: {}, // what to do here, keep substitues or always recalculate?
            }));
        });
    }, []);

    const reCalculateCart = useCallback(() => {
        removedItems.current = [];
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => {
                if (draft) {
                    draft.productChanges = {};
                }

                return draft; // what to do here, keep substitues or always recalculate?
            });
        });
    }, []);

    const removeItem = useCallback((item: RecipeItemState) => {
        removedItems.current.push(item);

        // fake cart recalculate
        setCartChangesState((prevChanges) => {
            return produce(prevChanges, (draft) => {
                if (draft) {
                    return { ...draft };
                }

                return draft;
            });
        });

        const recipeSection = getRecipeSection(item);

        ga4RecipesTracking.weeklyMenu.handleIngredient('empty')?.(
            recipeSection.recipeId,
            recipeSection.recipeName,
            item.id,
            item.name,
        );
    }, []);

    const {
        execute: resolve,
        result: resolvedRecipes,
        isLoading,
        error: resolveError,
    } = useAsyncDispatcher(
        async (request: ResolveRecipesRequest, storeId: string) => {
            // direct resolve to nortfork
            // const normalResolve = await recipeFlow.resolveGastrofyRecipes({
            //     options: {
            //         calculation: request.calculation,
            //         prioritization: request.prioritization || undefined,
            //         productExceptions: request.products?.map((x) => ({
            //             amount: x.qty || 0,
            //             externalId: x.productId,
            //             substituteIdentifier: x.substituteId,
            //         })),
            //     },
            //     recipes: request.recipes.map((x) => ({
            //         externalId: x.recipeId,
            //         portions: x.portions,
            //     })),
            //     storeId,
            // });

            return recipeCartQueue.run(async () =>
                recipeFlow.resolveRecipes(
                    {
                        calculation: request.calculation,
                        prioritization: request.prioritization,
                        recipes: request.recipes,
                        products: request.products,
                    },
                    storeId,
                ),
            );
        },
        {
            keepPreviousResult: true,
            initialLoadingState: initialLoading,
        },
    );

    useGroupErrorEffect(ErrorGroup.Global, !!resolveError, resolveError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedResolve = useCallback(_.debounce(resolve, 200), []);

    useEffect(() => {
        if (cartChangesState) {
            debouncedResolve(
                {
                    calculation: cartChangesState.calculation,
                    prioritization: cartChangesState.prioritization,
                    recipes: cartChangesState.recipes,
                    products: mapProductChangesToExceptions(cartChangesState.productChanges),
                },
                currentProductionUnit,
            );
        }
    }, [cartChangesState, currentProductionUnit, debouncedResolve]);

    const productChanges = useMemo(
        () => (cartChangesState ? Object.values(cartChangesState.productChanges) : []),
        [cartChangesState],
    );

    const isDirty = productChanges.length > 0 || removedItems.current.length;

    useEffect(() => {
        if (resolvedRecipes) {
            // we need to fake removing items, thats why i keep them in ref
            setCartState(createCartState(resolvedRecipes, removedItems.current));
        }
    }, [resolvedRecipes]);

    const { execute, result: recipeCartProducts } = useAsyncDispatcher(
        productFlow.getProductsByIds,
        {
            keepPreviousResult: true,
            initialResultState: [],
        },
    );

    useEffect(() => {
        if (cartState?.items && cartState.items.length) {
            execute(
                cartState.items.map((x) => x.id),
                currentProductionUnit,
            );
        }
    }, [execute, cartState, currentProductionUnit]);

    const getProductExceptions = () => {
        const changedSubstituteIds =
            cartChangesState && Object.keys(cartChangesState.productChanges);

        const changedItems = cartState?.items.filter(
            (item) =>
                changedSubstituteIds && changedSubstituteIds.includes(item.substituteIdentifier),
        );

        return changedItems;
    };

    return {
        updateItemQuantity,
        updateSubstituteProduct,
        updatePortions,
        updateCalculation,
        removeItem,
        calculateCart,
        reCalculateCart,
        recipeCart: cartState,
        recipeCartChanges: cartChangesState,
        getProductExceptions,
        recipeCartProducts,
        isLoading,
        isDirty,
        cartError: resolveError,
    };
};

export default useRecipeCart;
